import { h } from 'vue'
import { ElNotification } from 'element-plus'
import { WebScoketOnMessage, TaskStatusEnum } from '@/types/constantsEnum'
import type { WsResponse } from '@/utils/websocket'
import { useUserStoreHook } from '@/store/modules/user'
const userStore = useUserStoreHook()

interface NotificationOptions {
  title?: string
  message?: string
  type?: 'success' | 'warning' | 'info' | 'error'
  duration?: number
  customClass?: string
}

export class WebSocketNotification {
  private static defaultOptions: NotificationOptions = {
    duration: 5500,
    type: 'success',
  }

  static notify(messageType: string, data: WsResponse) {
    const options = this.getNotificationOptions(messageType, data)

    const msgType = data.data?.type || messageType
    // 如果是 VoiceTransformTaskWaitTime 类型的消息，直接返回不显示通知
    if (
      msgType === WebScoketOnMessage.VoiceTransformTaskWaitTime ||
      msgType === WebScoketOnMessage.VideoMixExport ||
      msgType === WebScoketOnMessage.CONVERSATION_MESSAGE
    ) {
      return
    }
    ElNotification({
      ...this.defaultOptions,
      ...options,
    })
  }

  private static getNotificationOptions(
    messageType: string,
    data: WsResponse,
  ): NotificationOptions {
    // 从消息数据中获取具体的消息类型

    const msgType = data.data?.type || messageType
    switch (msgType) {
      case WebScoketOnMessage.MusicCreationTaskUpdated:
        return this.getMusicCreationNotification(data)
      case WebScoketOnMessage.VoiceTransformTaskUpdated:
        return this.getVoiceTransformNotification(data)
      case WebScoketOnMessage.CreditUpdated:
        // 更新当前积分
        const scoreDetail = {
          credit: data.data?.credit,
        }
        userStore.setUserData('scoreDetail', scoreDetail)
        return this.getCreditUpdatedNotification(data)
      case WebScoketOnMessage.OrderSuccess:
        return this.getOrderSuccessNotification(data)
      case WebScoketOnMessage.VideoCreationTaskUpdated:
        return this.getVideoCreationTaskNotification(data)
      case WebScoketOnMessage.LyricsSceneTaskUpdated:
        return this.getLyricsSceneTaskNotification(data)
      case WebScoketOnMessage.ImageCreationTaskUpdated:
        return this.getImageCreationTaskNotification(data)
      case WebScoketOnMessage.VoiceTransformTaskWaitTime || WebScoketOnMessage.VideoMixExport:
        return {
          title: '',
          message: '',
          type: 'info',
          duration: 0,
        }

      default:
        return {
          title: '系统通知',
          message: '收到新消息',
          type: 'info',
        }
    }
  }

  private static getMusicCreationNotification(data: WsResponse): NotificationOptions {
    const status = data.data?.status || 'success'
    console.log(data, '音乐创作任务更新音乐创作任务更新data.data')
    return {
      title: '音乐创作任务更新',
      message: `任务《${data.data?.taskId}》${this.getStatusText(status)}`,
      type: this.getStatusType(status),
      customClass: 'music-creation-notification',
    }
  }

  private static getVoiceTransformNotification(data: WsResponse): NotificationOptions {
    const status = data.data?.status || 'success'
    return {
      title: 'AI演唱任务更新',
      message: `任务《${data.data?.taskId}》${this.getStatusText(status)}`,
      type: this.getStatusType(status),
      customClass: 'voice-transform-notification',
    }
  }

  private static getCreditUpdatedNotification(data: WsResponse): NotificationOptions {
    return {
      title: '积分更新',
      message: `积分已更新为:${data.data?.credit}`,
      type: 'success',
      customClass: 'credit-updated-notification',
    }
  }

  private static getOrderSuccessNotification(data: WsResponse): NotificationOptions {
    return {
      title: '订单支付成功',
      message: `订单已支付成功，订单号为《${data.data?.orderId}》`,
      type: 'success',
      customClass: 'order-success-notification',
    }
  }
  private static getUserEventNotification(data: WsResponse): NotificationOptions {
    return {
      title: '用户事件',
      message: '用户状态已更新',
      type: 'info',
      customClass: 'user-event-notification',
    }
  }
  private static getTaskWaitTimeNotification(data: WsResponse): NotificationOptions {
    return {
      title: 'AI演唱任务等待时间',
      message: `任务《${data.data?.taskId}》等待时间:${data.data?.waitTime}分钟`,
      type: 'info',
    }
  }
  private static getVideoCreationTaskNotification(data: WsResponse): NotificationOptions {
    return {
      title: '视频创作任务更新',
      message: `任务《${data.data?.taskId}》${this.getStatusText(status)}`,
    }
  }
  private static getImageCreationTaskNotification(data: WsResponse): NotificationOptions {
    return {
      title: '图片创作任务更新',
      message: `任务《${data.data?.taskId}》${this.getStatusText(status)}`,
    }
  }
  private static getLyricsSceneTaskNotification(data: WsResponse): NotificationOptions {
    return {
      title: '歌词分镜任务更新',
      message: `任务《${data.data?.taskId}》${this.getStatusText(status)}`,
    }
  }

  // 获取状态文本
  private static getStatusText(status: TaskStatusEnum): string {
    switch (status) {
      case TaskStatusEnum.COMPLETED:
        return '已完成'
      case TaskStatusEnum.FAILED || TaskStatusEnum.BLOCKED:
        return '失败'
      case TaskStatusEnum.PROCESSING || TaskStatusEnum.PENDING:
        return '处理中'
      case TaskStatusEnum.CANCELLED:
        return '已取消'
      default:
        return '状态更新'
    }
  }

  // 获取状态对应的通知类型
  private static getStatusType(status: TaskStatusEnum): NotificationOptions['type'] {
    switch (status) {
      case TaskStatusEnum.COMPLETED:
        return 'success'
      case TaskStatusEnum.FAILED || TaskStatusEnum.BLOCKED:
        return 'error'
      case TaskStatusEnum.PROCESSING || TaskStatusEnum.PENDING:
        return 'info'
      default:
        return 'info'
    }
  }
}
